import React from 'react';
import { History, ArrowRight, Plus, Trash2, Edit3 } from 'lucide-react';

const REASON_LABEL = {
  stock_out:        'Out of stock — substituted',
  patient_request:  'Patient preference',
  doctor_approved:  'Doctor verbally approved',
  price_correction: 'Price / GST correction',
  strength_swap:    'Strength / formulation swap',
  other:            'Other',
};

/**
 * PharmacyAuditTrail — renders a compact, scannable diff of what the
 * pharmacist changed between the doctor's prescription and what was
 * actually dispensed. Used in two places:
 *   1. PharmacyReceipt drawer (under the items table)
 *   2. VisitHistoryDrawer timeline tab (per-visit dispense card)
 *
 * Props:
 *   entries  Array<{action, medicine, changes?, details?, by, at, reason}>
 *   compact  boolean — denser rows for timeline
 */
export default function PharmacyAuditTrail({ entries = [], compact = false }) {
  if (!entries || entries.length === 0) return null;
  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        timeZone: 'Asia/Kolkata',
      });
    } catch { return ''; }
  };
  return (
    <section className={compact ? '' : 'rounded-lg border border-amber-200 bg-amber-50/40 p-3'}
             data-testid="pharmacy-audit-trail">
      {!compact && (
        <div className="flex items-center gap-2 mb-2">
          <History className="h-3.5 w-3.5 text-amber-700" />
          <h4 className="text-[11px] uppercase tracking-wider font-bold text-amber-800">
            Pharmacy adjustments ({entries.length})
          </h4>
        </div>
      )}
      <ul className="space-y-1.5">
        {entries.map((e, i) => (
          <li key={i} className={`text-[11px] flex items-start gap-2 ${compact ? '' : 'leading-relaxed'}`}
              data-testid={`audit-entry-${i}`}>
            <ActionIcon action={e.action} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className={`font-bold ${
                  e.action === 'added'    ? 'text-emerald-700' :
                  e.action === 'removed'  ? 'text-red-700' :
                                             'text-amber-800'
                }`}>{titleFor(e.action)}</span>
                <span className="font-semibold text-zinc-900 truncate">{e.medicine}</span>
                {e.reason && (
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white text-amber-700 border border-amber-200">
                    {REASON_LABEL[e.reason] || e.reason}
                  </span>
                )}
              </div>
              {/* Field-level diffs for modified rows */}
              {e.action === 'modified' && (e.changes || []).length > 0 && (
                <div className="mt-0.5 flex flex-wrap gap-1.5">
                  {e.changes.map((c, j) => (
                    <span key={j} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white border border-amber-200 rounded text-[10px] font-mono">
                      <span className="text-zinc-500 uppercase tracking-wider font-sans font-bold">{c.field}</span>
                      <span className="text-zinc-400 line-through">{String(c.from ?? '—')}</span>
                      <ArrowRight className="h-2.5 w-2.5 text-amber-600" />
                      <span className="text-amber-900 font-bold">{String(c.to ?? '—')}</span>
                    </span>
                  ))}
                </div>
              )}
              {/* Details for added / removed rows */}
              {(e.action === 'added' || e.action === 'removed') && e.details && (
                <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                  Qty {e.details.qty ?? '—'}{e.details.uom ? ` ${e.details.uom}` : ''}
                  {e.details.price != null ? ` · ₹${e.details.price}` : ''}
                  {e.details.gst_percent != null ? ` · GST ${e.details.gst_percent}%` : ''}
                </div>
              )}
              {/* Provenance line */}
              <div className="text-[10px] text-zinc-400 mt-0.5">
                {e.by ? `by ${e.by}` : ''}{e.at ? ` · ${fmtTime(e.at)}` : ''}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ActionIcon({ action }) {
  if (action === 'added')   return <Plus    className="h-3 w-3 text-emerald-600 mt-0.5 flex-shrink-0" />;
  if (action === 'removed') return <Trash2  className="h-3 w-3 text-red-600 mt-0.5 flex-shrink-0" />;
  return                            <Edit3   className="h-3 w-3 text-amber-700 mt-0.5 flex-shrink-0" />;
}

function titleFor(action) {
  if (action === 'added')   return 'Added';
  if (action === 'removed') return 'Removed';
  return 'Edited';
}
